import { useCallback, useEffect, useSyncExternalStore } from 'react';

/**
 * 미읽음 카운터 (PLAN 6-8장).
 *
 * 서버에 미읽음 수를 저장하지 않는다. sender가 다른 멤버의 `members/{uid}.unreadCount`를
 * 올리려면 Rules를 우회하는 Functions fanout이 필요한데(PLAN 5-8-2장), 그건 M4의
 * 집계 배치 작업이다. 그때까지는 **이미 구독 중인 대화 목록 스냅샷만으로** 계산한다:
 * `lastMessage`가 앞으로 나아간 방을 세고, 방을 열면 0으로 되돌린다.
 *
 * 값은 기기 로컬(localStorage)이며 읽어 본 마지막 시각을 배지 초기화 후에도 유지한다.
 * 기존 멤버 조회의 lastReadAt으로 앱 시작 시 오래된 로컬 배지도 보정한다.
 * 추가 읽기/쓰기 비용은 0이다.
 */
const STORAGE_PREFIX = 'talk2nick.unread.';
const EMPTY: Record<string, number> = {};

const listeners = new Set<() => void>();
let activeUid: string | null = null;
let state: Record<string, number> = {};
/** 목록 스냅샷에서 이미 본 마지막 메시지 시각 (방별) */
let seenAt: Record<string, number> = {};

function notify(): void {
  syncAppBadge(computeTotal(activeUid ? state : EMPTY));
  for (const listener of listeners) listener();
}

function readStorage(uid: string): {
  counts: Record<string, number>;
  seen: Record<string, number>;
} {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + uid);
    if (!raw) return { counts: {}, seen: {} };
    const parsed = JSON.parse(raw) as {
      counts?: Record<string, number>;
      seen?: Record<string, number>;
    };
    return { counts: parsed.counts ?? {}, seen: parsed.seen ?? {} };
  } catch {
    return { counts: {}, seen: {} };
  }
}

function persist(): void {
  if (!activeUid) return;
  try {
    window.localStorage.setItem(
      STORAGE_PREFIX + activeUid,
      JSON.stringify({ counts: state, seen: seenAt }),
    );
  } catch {
    // 저장 실패는 치명적이지 않다 — 세션 동안만 배지가 사라진다
  }
}

function setActiveUser(uid: string | null): void {
  if (activeUid === uid) return;
  activeUid = uid;
  if (uid) {
    const stored = readStorage(uid);
    state = stored.counts;
    seenAt = stored.seen;
  } else {
    state = EMPTY;
    seenAt = {};
  }
  notify();
}

/**
 * 목록 스냅샷을 미읽음으로 환산한다.
 *
 * - `activeConvoId` 또는 서버 lastReadAt까지 읽은 방은 기존 배지도 지운다
 * - 처음 보는 방인데 마지막 메시지가 내 것이 아니면 1건으로 잡는다
 *   (앱을 닫아 둔 동안 온 메시지)
 * - 이후에는 마지막 메시지 시각이 앞으로 갈 때마다 1건씩 늘린다
 * - `isLoaded`: 활성 대화 목록이 로드된 상태일 때 목록에서 삭제된 방의 미읽음과 시각을 자동 정리한다
 */
export function observeConvoPreviews(
  previews: { convoId: string; at: number; senderId?: string; lastReadAt?: number }[],
  myUid: string,
  activeConvoId: string | null,
  isLoaded = true,
): void {
  if (!myUid) return;
  if (activeUid !== myUid) {
    setActiveUser(myUid);
  }

  let changed = false;

  // 활성 대화 목록이 완전히 로드된 경우, 삭제되었거나 제외된 방의 미읽음 정리
  if (isLoaded) {
    const currentConvoIds = new Set(previews.map((p) => p.convoId));
    let nextState: Record<string, number> | null = null;
    for (const convoId of Object.keys(state)) {
      if (!currentConvoIds.has(convoId)) {
        if (!nextState) nextState = { ...state };
        delete nextState[convoId];
        delete seenAt[convoId];
        changed = true;
      }
    }
    if (nextState) {
      state = nextState;
    }
    for (const convoId of Object.keys(seenAt)) {
      if (!currentConvoIds.has(convoId)) {
        delete seenAt[convoId];
        changed = true;
      }
    }
  }

  for (const { convoId, at, senderId, lastReadAt = 0 } of previews) {
    if (!at || at <= 0) continue;
    if (convoId === activeConvoId || lastReadAt >= at) {
      const nextSeen = Math.max(seenAt[convoId] ?? 0, at, lastReadAt);
      if (seenAt[convoId] !== nextSeen) {
        seenAt[convoId] = nextSeen;
        changed = true;
      }
      if (state[convoId] !== undefined) {
        const next = { ...state };
        delete next[convoId];
        state = next;
        changed = true;
      }
      continue;
    }
    const previous = seenAt[convoId];
    const isMine = senderId === myUid;
    if (previous === undefined) {
      seenAt[convoId] = at;
      changed = true;
      if (senderId && !isMine) {
        state = { ...state, [convoId]: (state[convoId] ?? 0) + 1 };
        changed = true;
      }
      continue;
    }
    if (at > previous) {
      seenAt[convoId] = at;
      changed = true;
      if (senderId && !isMine) {
        state = { ...state, [convoId]: (state[convoId] ?? 0) + 1 };
        changed = true;
      }
    }
  }
  if (changed) {
    persist();
    notify();
  }
}

export function clearUnreadFor(convoId: string): void {
  if (!activeUid) return;
  const hasCount = state[convoId] !== undefined;
  if (!hasCount) return;

  if (hasCount) {
    const next = { ...state };
    delete next[convoId];
    state = next;
  }
  // Keep the watermark: the next list snapshot must not recount this message.
  persist();
  notify();
}

export function clearAllUnread(): void {
  if (!activeUid) return;
  if (Object.keys(state).length === 0) return;
  state = EMPTY;
  persist();
  notify();
}

export function unreadCountFor(convoId: string): number {
  return state[convoId] ?? 0;
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): Record<string, number> {
  return activeUid ? state : EMPTY;
}

/** 서버 렌더 스냅샷 (이 앱은 CSR이지만 useSyncExternalStore 계약상 필요하다) */
function getServerSnapshot(): Record<string, number> {
  return EMPTY;
}

/** OS 홈 화면/독 앱 아이콘 배지 동기화 (iOS 16.4+ PWA, Android Chrome, macOS Safari, Windows Chrome) */
export function syncAppBadge(total: number): void {
  if (typeof navigator === 'undefined') return;
  const nav = navigator as Navigator & {
    setAppBadge?: (contents?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  try {
    if (total > 0 && typeof nav.setAppBadge === 'function') {
      void nav.setAppBadge(total).catch(() => undefined);
    } else if (total <= 0 && typeof nav.clearAppBadge === 'function') {
      void nav.clearAppBadge().catch(() => undefined);
    }
  } catch {
    // 미지원 브라우저/환경 무시
  }
}

function computeTotal(counts: Record<string, number>): number {
  return Math.max(
    0,
    Object.values(counts).reduce(
      (sum, n) => sum + (typeof n === 'number' && Number.isFinite(n) ? n : 0),
      0,
    ),
  );
}

/** 미읽음 카운트 구독 + 방 열기 시 초기화 헬퍼 */
export function useUnread(uid: string | null) {
  useEffect(() => {
    setActiveUser(uid);
  }, [uid]);

  const counts = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const clear = useCallback((convoId: string) => clearUnreadFor(convoId), []);
  const clearAll = useCallback(() => clearAllUnread(), []);
  const total = computeTotal(counts);

  useEffect(() => {
    syncAppBadge(total);
  }, [total]);

  return { counts, total, clear, clearAll };
}
